import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type Hls from "hls.js";
import type { RoomMedia } from "@yay/shared";
import { config } from "../config";
import { YouTubePlayer } from "./YouTubePlayer";

type PlayerState = "loading" | "playing" | "error";

interface VideoPlayerProps {
  media?: RoomMedia;
  /** Visually hidden but kept mounted (full-nerd layout). */
  stowed?: boolean;
  /** Client↔server clock offset (ms) — compensates wall-clock anchor skew. */
  clockOffsetMs?: number;
  onSyncMedia?: (sync: { playing?: boolean; positionSec?: number }) => void;
}

function mediaPlaybackEqual(a: RoomMedia, b: RoomMedia): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "youtube" && b.kind === "youtube") {
    if (a.youtubeId !== b.youtubeId) return false;
    const ap = a.playback;
    const bp = b.playback;
    if (!ap && !bp) return true;
    if (!ap || !bp) return false;
    return (
      ap.at === bp.at &&
      ap.playing === bp.playing &&
      ap.positionSec === bp.positionSec
    );
  }
  return true;
}

export const VideoPlayer = memo(function VideoPlayer({
  media = { kind: "default" },
  stowed = false,
  clockOffsetMs = 0,
  onSyncMedia,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const initRef = useRef(0);
  const [playerState, setPlayerState] = useState<PlayerState>("loading");
  const [muted, setMuted] = useState(true);
  const [errorText, setErrorText] = useState("");

  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolume, setShowVolume] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const isYouTube = media.kind === "youtube" && !!media.youtubeId;

  const setup = useCallback(() => {
    const video = videoRef.current;
    if (!video || isYouTube) return;

    const runId = ++initRef.current;
    setPlayerState("loading");
    setErrorText("");
    setMuted(true);
    setPlaying(false);

    let hls: Hls | null = null;
    let usedFallback = false;
    let started = false;
    let stallTimer = 0;

    // A manifest that loads but never starts playing (stall: no fatal hls.js
    // error, no `playing` event) used to leave the player stuck on "taking
    // a while" forever — the fallback only engaged on FATAL errors. Fail
    // over once after a stall window, then surface an error with a Retry.
    const failOver = () => {
      if (hls) {
        hls.loadSource(config.hlsFallback);
      } else if (video) {
        video.src = config.hlsFallback;
        void video.play().catch(() => {});
      }
    };
    const armStallTimer = () => {
      window.clearTimeout(stallTimer);
      stallTimer = window.setTimeout(() => {
        if (runId !== initRef.current || started) return;
        if (!usedFallback) {
          usedFallback = true;
          setErrorText("Switching streams…");
          failOver();
          armStallTimer();
        } else {
          setPlayerState("error");
          setErrorText("Stream unavailable right now. Try again later.");
        }
      }, 12_000);
    };
    armStallTimer();

    function onPlaying() {
      if (runId !== initRef.current) return;
      started = true;
      setPlayerState("playing");
      setPlaying(true);
      window.clearTimeout(stallTimer);
    }

    function onPause() {
      setPlaying(false);
    }

    function onVolumeChange() {
      if (video) {
        setVolume(video.volume);
        setMuted(video.muted);
      }
    }

    video.addEventListener("playing", onPlaying);
    video.addEventListener("pause", onPause);
    video.addEventListener("volumechange", onVolumeChange);

    function tryPlay() {
      video?.play().catch(() => {});
    }

    void (async () => {
      let HlsModule: typeof Hls;
      try {
        HlsModule = (await import("hls.js")).default;
      } catch {
        if (runId !== initRef.current) return;
        setPlayerState("error");
        setErrorText("Failed to load video player. Check your connection.");
        window.clearTimeout(stallTimer);
        return;
      }

      if (runId !== initRef.current || !video) return;

      if (HlsModule.isSupported()) {
        hls = new HlsModule({
          enableWorker: true,
          lowLatencyMode: false,
        });
        hlsRef.current = hls;
        hls.loadSource(config.hlsUrl);
        hls.attachMedia(video);
        hls.on(HlsModule.Events.MANIFEST_PARSED, () => {
          if (runId === initRef.current) tryPlay();
        });
        hls.on(HlsModule.Events.ERROR, (_event, data) => {
          if (runId !== initRef.current || !data.fatal || !hls) return;
          if (!usedFallback) {
            usedFallback = true;
            hls.loadSource(config.hlsFallback);
          } else {
            setPlayerState("error");
            setErrorText("Stream unavailable right now. Try again later.");
          }
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = config.hlsUrl;
        tryPlay();
      } else {
        setPlayerState("error");
        setErrorText("Your browser doesn't support HLS playback.");
      }
    })();

    return () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("volumechange", onVolumeChange);
      window.clearTimeout(stallTimer);
      if (hls) {
        hls.destroy();
        hls = null;
        hlsRef.current = null;
      }
    };
  }, [isYouTube]);

  useEffect(() => {
    if (isYouTube) {
      initRef.current += 1;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      setPlayerState("playing");
      return;
    }
    const cleanup = setup();
    return () => cleanup?.();
  }, [setup, isYouTube, media.youtubeId]);

  // Resume only when transitioning stowed → visible (not on every render).
  // While stowed, stay paused — focus/visibility handlers must not call play()
  // or some browsers briefly composite a frame of the stream.
  const wasStowedRef = useRef(stowed);
  const stowedRef = useRef(stowed);
  stowedRef.current = stowed;
  useEffect(() => {
    const wasStowed = wasStowedRef.current;
    wasStowedRef.current = stowed;
    const video = videoRef.current;
    if (stowed) {
      if (video && !isYouTube && !video.paused) video.pause();
      return;
    }
    if (!wasStowed || isYouTube || !video) return;
    const id = requestAnimationFrame(() => {
      if (stowedRef.current) return;
      if (video.paused) video.play().catch(() => {});
    });
    return () => cancelAnimationFrame(id);
  }, [stowed, isYouTube]);

  // Snap back to the live edge whenever the viewer returns to the tab, so a
  // stream left idle for a while resumes at "now" instead of a stale buffer.
  useEffect(() => {
    if (isYouTube) return;
    function jumpToLive() {
      if (stowedRef.current) return;
      const video = videoRef.current;
      if (!video || document.visibilityState !== "visible") return;

      const hls = hlsRef.current;
      const livePos =
        hls && typeof hls.liveSyncPosition === "number"
          ? hls.liveSyncPosition
          : null;
      const seekable = video.seekable;
      const edge =
        livePos ??
        (seekable.length > 0 ? seekable.end(seekable.length - 1) : null);

      // Only jump forward, and only if we've fallen meaningfully behind live.
      if (edge !== null && edge - video.currentTime > 5) {
        try {
          video.currentTime = edge;
        } catch {
          /* not seekable yet */
        }
      }
      if (video.paused) video.play().catch(() => {});
    }

    document.addEventListener("visibilitychange", jumpToLive);
    window.addEventListener("focus", jumpToLive);
    window.addEventListener("online", jumpToLive);
    return () => {
      document.removeEventListener("visibilitychange", jumpToLive);
      window.removeEventListener("focus", jumpToLive);
      window.removeEventListener("online", jumpToLive);
    };
  }, [isYouTube]);

  const handleUnmute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    setMuted(false);
    video.play().catch(() => {});
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }, []);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current;
      if (!video) return;
      const v = parseFloat(e.target.value);
      video.volume = v;
      setVolume(v);
      if (v > 0 && video.muted) {
        video.muted = false;
        setMuted(false);
      }
    },
    [],
  );

  const toggleFullscreen = useCallback(() => {
    const el = videoRef.current?.parentElement;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  const scheduleHideControls = useCallback(() => {
    window.clearTimeout(hideTimerRef.current);
    setShowControls(true);
    hideTimerRef.current = setTimeout(() => {
      setShowControls(false);
      setShowVolume(false);
    }, 2500);
  }, []);

  useEffect(() => {
    return () => window.clearTimeout(hideTimerRef.current);
  }, []);

  const isMutedOrSilent = muted || volume === 0;

  if (isYouTube && media.youtubeId) {
    return (
      <YouTubePlayer
        youtubeId={media.youtubeId}
        playback={media.playback}
        stowed={stowed}
        clockOffsetMs={clockOffsetMs}
        onSync={onSyncMedia ?? (() => {})}
      />
    );
  }

  return (
    <div
      className={`player-container${showControls ? " controls-visible" : ""}`}
      onMouseMove={scheduleHideControls}
      onMouseLeave={() => {
        setShowControls(false);
        setShowVolume(false);
      }}
    >
      <video
        ref={videoRef}
        className={`video-player${playerState === "playing" ? " visible" : ""}`}
        autoPlay
        muted
        playsInline
        onClick={togglePlay}
      />

      {/* --- loading --- */}
      {playerState === "loading" && (
        <div className="player-overlay player-loading">
          <div className="player-glow" />
          <div className="player-spinner" />
          <p>Loading stream{errorText ? " — " + errorText : "..."}</p>
          {errorText && (
            <button className="player-retry-btn" onClick={setup}>
              Retry
            </button>
          )}
        </div>
      )}

      {/* --- unmute prompt --- */}
      {playerState === "playing" && isMutedOrSilent && (
        <button
          className="player-overlay player-unmute"
          onClick={handleUnmute}
          aria-label="Unmute video"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 5 6 9 2 9 2 15 6 15 11 19 11 5Z" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
          <span>Click to unmute</span>
        </button>
      )}

      {/* --- error --- */}
      {playerState === "error" && (
        <div className="player-overlay player-error">
          <div className="player-error-badge">!</div>
          <p>{errorText}</p>
          <button className="player-retry-btn" onClick={setup}>
            Retry
          </button>
        </div>
      )}

      {/* --- custom controls --- */}
      {playerState === "playing" && (
        <div className="player-controls">
          <div className="controls-left">
            <button
              className="control-btn"
              onClick={togglePlay}
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="6,3 20,12 6,21" />
                </svg>
              )}
            </button>

            <span className="live-badge">
              <span className="live-dot" />
              LIVE
            </span>
          </div>

          <div className="controls-right">
            <div
              className="volume-group"
              onMouseEnter={() => setShowVolume(true)}
              onMouseLeave={() => setShowVolume(false)}
            >
              <button
                className="control-btn"
                onClick={toggleMute}
                aria-label={isMutedOrSilent ? "Unmute" : "Mute"}
              >
                {isMutedOrSilent ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 5 6 9 2 9 2 15 6 15 11 19 11 5Z" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 5 6 9 2 9 2 15 6 15 11 19 11 5Z" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                )}
              </button>
              <div className={`volume-slider-wrap${showVolume ? " open" : ""}`}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={isMutedOrSilent ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="volume-slider"
                  aria-label="Volume"
                />
              </div>
            </div>

            <button
              className="control-btn"
              onClick={toggleFullscreen}
              aria-label="Fullscreen"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  return (
    prev.stowed === next.stowed &&
    prev.onSyncMedia === next.onSyncMedia &&
    prev.clockOffsetMs === next.clockOffsetMs &&
    mediaPlaybackEqual(prev.media ?? { kind: "default" }, next.media ?? { kind: "default" })
  );
});
